import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CardContent, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PartialLabelData, WhatsAppTemplatesContent } from '../../api';
import { useLanguage } from '../../common/GeneralUtils';
import { useGetAccountAuthenticated } from '../../session/swr/useGetAccountAuthenticated';

type WhatsAppTemplatePreviewProps = {
  templateType: 'confirmation' | 'reminder' | 'pickup' | 'post_workshop';
  language: 'en' | 'he';
  templates?: WhatsAppTemplatesContent | null;
  extraLines: Array<PartialLabelData>;
};

export const WhatsAppTemplatePreview = ({
  templateType,
  language,
  templates,
  extraLines,
}: WhatsAppTemplatePreviewProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const currentLanguage = useLanguage();
  const shouldRenderRTL = language !== 'he' && currentLanguage === 'he';
  const { account } = useGetAccountAuthenticated({});
  const firstOrderPage = account?.experiences[0]?.order_pages?.[0];
  // Dummy data for preview
  const dummyData = useMemo(() => {
    if (language === 'he') {
      return {
        customerName: 'נטע ויזול',
        orderNumber: '123123',
        activityName: firstOrderPage?.label[language] || 'ניפוח זכוכית',
        dateTime: '2024.10.12 11:00',
        location: account.locations[0]?.address?.[language] || 'המיקום שלנו',
        extraLine1: extraLines[0]?.[language] || '',
        extraLine2: extraLines[1]?.[language] || '',
        extraLine3: extraLines[2]?.[language] || '',
      };
    }
    return {
      customerName: 'John Doe',
      orderNumber: '123123',
      activityName: firstOrderPage?.label[language] || 'Glass Blowing',
      dateTime: '2024.10.12 11:00',
      location: account.locations[0]?.address?.[language] || 'Our location',
      extraLine1: extraLines[0]?.[language] || '',
      extraLine2: extraLines[1]?.[language] || '',
      extraLine3: extraLines[2]?.[language] || '',
    };
  }, [language, extraLines]);

  const processTemplate = (template: string, data: typeof dummyData): string => {
    if (templateType === 'pickup') {
      // Pickup template has different variable mapping
      return template
        .replace('{0}', data.customerName)
        .replace('{1}', data.extraLine1)
        .replace('{2}', data.extraLine2)
        .replace('{3}', data.extraLine3)
        .replace('{4}', data.orderNumber);
    }

    if (templateType === 'post_workshop') {
      return template
        .replace('{0}', data.customerName)
        .replace('{1}', data.activityName)
        .replace('{2}', data.dateTime)
        .replace('{3}', data.location)
        .replace('{4}', data.extraLine1)
        .replace('{5}', data.extraLine2)
        .replace('{6}', data.orderNumber);
    }

    // Standard mapping for confirmation and reminder templates
    return template
      .replace('{0}', data.customerName)
      .replace('{1}', data.orderNumber)
      .replace('{2}', data.activityName)
      .replace('{3}', data.dateTime)
      .replace('{4}', data.location)
      .replace('{5}', data.extraLine1)
      .replace('{6}', data.extraLine2)
      .replace('{7}', data.extraLine3);
  };

  const getTemplateContent = (): string => {
    // Get base template from templates prop
    const baseTemplate = templates?.templates?.[`${templateType}_template`]?.[language];

    if (baseTemplate) {
      return processTemplate(baseTemplate, dummyData);
    }

    // Fallback to default template structure
    let defaultTemplate: string;
    if (templateType === 'confirmation') {
      defaultTemplate =
        language === 'he'
          ? 'שלום {0}! ההזמנה שלך #{1} ל{2} ב{3} מאושרת. נתראה ב{4}.\n\n {5}\n\n {6}\n\nפרטי ההזמנה המלאים והחשבונית אצלכם במייל.\nיום נעים מ www.monkeybook.io'
          : 'Hey {0}!\nYour booking #{1} for {2} on {3} is confirmed.\nSee you at {4}.\n\n{5}\n\n{6}\n\nThe full booking details and invoice have been sent to your email.\nHave a great day from www.monkeybook.io';
    } else if (templateType === 'post_workshop') {
      defaultTemplate =
        language === 'he'
          ? 'שלום {0}! תודה שהשתתפתם ב{1} בתאריך {2} ב{3}. מקווים שנהניתם!\n\n{4}\n\n{5}\n\nמספר הזמנה #{6}.\nיום נעים מ www.monkeybook.io.'
          : 'Hi {0}! Thank you for joining {1} on {2} at {3}. We hope you had a great time!\n\n{4}\n\n{5}\n\nOrder number #{6}.\nHave a great day from www.monkeybook.io.';
    } else if (templateType === 'pickup') {
      defaultTemplate =
        language === 'he'
          ? 'שלום {0}! הכלים שלכם מהסדנה מוכנים!\n{1}\n{2}\n{3}\n\nמספר הזמנה #{4}.\nיום נעים מ www.monkeybook.io.'
          : 'Hi {0}! Your items from workshop are ready for pickup! 🏺✨\n\n{1}\n\n{2}\n\n{3}\n\nOrder number #{4}.\nHave a great day from www.monkeybook.io.';
    } else {
      defaultTemplate =
        language === 'he'
          ? 'שלום {0}! נשמח להזכיר לכם לגבי ההזמנה שלכם שמספרה {1}.\nזוהי תזכורת ל{2} ב {3} ב{4}.\n\n {5}\n\n {6}\n\nיום נעים מ www.monkeybook.io.'
          : "Hello {0}! We'd like to remind you about your booking #{1}.\nThis is a reminder for {2} on {3} at {4}.\n\n {5}\n\n {6}\n\nHave a great day from www.monkeybook.io.";
    }

    return processTemplate(defaultTemplate, dummyData);
  };

  const previewContent = getTemplateContent();

  const getMessageTitle = (): string => {
    const accountLabel = account?.label?.[language] || account?.label?.en || 'Business Name';

    if (templateType === 'confirmation') {
      return language === 'he' ? `אישור הזמנה עבור ${accountLabel}` : `Order Confirmation for ${accountLabel}`;
    }
    if (templateType === 'pickup') {
      return language === 'he' ? `איסוף מוכן עבור ${accountLabel}` : `Pickup Ready for ${accountLabel}`;
    }
    if (templateType === 'post_workshop') {
      return language === 'he' ? `תודה שהגעתם ל${accountLabel}` : `Thank You from ${accountLabel}`;
    }
    return language === 'he' ? `תזכורת עבור ${accountLabel}` : `Reminder for ${accountLabel}`;
  };

  return (
    <Card
      sx={{
        maxWidth: isSmallScreen ? '100%' : 400,
        backgroundImage: 'url(https://i.pinimg.com/564x/45/87/db/4587dba198ee103cf89c1f9c8828a1f4.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'repeat',
        backgroundPosition: 'center',
        borderRadius: '8px',
        minHeight: 200,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '8px',
          zIndex: 1,
        },
      }}>
      <CardContent sx={{ pb: 2, position: 'relative', zIndex: 2 }}>
        <Typography
          variant="caption"
          sx={{
            mb: 1,
            display: 'block',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '4px 8px',
            borderRadius: '8px',
            color: '#333',
            fontSize: '12px',
            fontWeight: 600,
          }}>
          {t('whatsappSettings.preview.label')} ({language === 'he' ? 'עברית' : 'English'})
        </Typography>

        <Box
          sx={{
            backgroundColor: '#dcf8c6', // WhatsApp message green
            borderRadius: '8px',
            p: 2,
            position: 'relative',
            direction: shouldRenderRTL ? 'rtl' : 'ltr',
            textAlign: shouldRenderRTL ? 'right' : 'left',
            marginLeft: 'auto',
            marginRight: 'auto',
            maxWidth: '85%',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              [shouldRenderRTL ? 'right' : 'left']: -8,
              width: 0,
              height: 0,
              borderTop: '8px solid #dcf8c6',
              borderLeft: shouldRenderRTL ? 'none' : '8px solid transparent',
              borderRight: shouldRenderRTL ? '8px solid transparent' : 'none',
            },
          }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              color: '#000',
              mb: 1,
            }}>
            {getMessageTitle()}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontFamily: 'system-ui, -apple-system, sans-serif',
              lineHeight: 1.4,
              whiteSpace: 'pre-line',
              color: '#000',
              fontSize: '15px',
              fontWeight: 400,
            }}>
            {previewContent}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: shouldRenderRTL ? 'right' : 'left',
              mt: 1,
              color: '#666',
              fontSize: '11px',
            }}>
            {new Date().toLocaleTimeString(language === 'he' ? 'he-IL' : 'en-US', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
